import React, { useEffect, useState } from 'react'
import { Table } from 'react-bootstrap';
import { ExerciseStatus } from "../../../constants/exercises";
import { pages } from '../../../constants/pages';
import ExerciseComplete from '../../common/components/ExerciseComplete';
import ExerciseIntro from '../../common/components/ExerciseIntro';
import FooterButtons from '../../common/components/FooterButtons';
import { getCenteredBreathingText, getCognitiveFooterButtons, getTotalMinutes } from '../../common/exerciseHelper';
import { Gender, genderLabels, getCurrent, getLastSelectedLevelAndGender, getResults, isReadyToStart } from '../ntfHelper';
import { Card, NtfExercises as NtfExercisesType, NtfUserScore } from '../ntfSlice';
import NtfInProgress from './NtfInProgress';
export interface INtfExercisesProps {
  status: ExerciseStatus
  exercises?: NtfExercisesType
  cards?: Card[]
  userScores: NtfUserScore[]
  startTime?: number
  endTime?: number
  loadCards: (level: number, gender: Gender | undefined) => void
  endExercise: (finished: boolean, attemps: number) => void
  next: () => void
  backTo: (route: string) => void
}

const NtfExercises = ({ status, exercises, cards, userScores, startTime, endTime, next, loadCards, endExercise, backTo }: INtfExercisesProps) => {
  const [selectedLevel, setSelectedLevel] = useState<number | undefined>(undefined)

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const handleNext = () => {
    window.scrollTo(0, 0)
    next()
  }

  const handleRepeat = () => {
    window.scrollTo(0, 0)
    const levelAndGender = getLastSelectedLevelAndGender(userScores) 
    if (levelAndGender) {
      const { level, gender } = levelAndGender
      setSelectedLevel(level)
      loadCards(level, gender)
    }
  }

  const handlePickLevelGender = (level: number, gender: Gender | undefined) => {
    window.scrollTo(0, 0)
    setSelectedLevel(level)
    loadCards(level, gender)
  }

  const handleFinishExercise = (attemps: number) => {
    window.scrollTo(0, 0)
    setSelectedLevel(undefined)
    endExercise(true, attemps)
  }

  const { currentExercise } = getCurrent(userScores)

  const totalMinutes = getTotalMinutes(startTime, endTime)

  const results = getResults(status, userScores)

  const completedExercises = currentExercise - 1
  
  return (
    <>
      <ExerciseIntro status={status}>
        <h4>Instructions</h4>
        <p><em><strong>Name the Face</strong></em> provides an opportunity to improve attention and memory skills. Each exercise begins with a display of faces and accompanying names. Study these, and when you feel that you have the names and faces memorized, click “next.” On the next screen, you will turn cards over to find the matched pairs (a name and a face). As you progress through the levels, the exercise becomes more challenging. Your objective is to complete each exercise in the least amount of time and with the least number of attempts needed. </p>
      </ExerciseIntro>
      <NtfInProgress
        status={status}
        exercises={exercises}
        currentExercise={currentExercise}
        cards={cards}
        pickLevelAndGender={handlePickLevelGender}
        finishExercise={handleFinishExercise}
        selectedLevel={selectedLevel}
      />
      <ExerciseComplete status={status} category="cognitive" totalMinutes={totalMinutes} completedExercises={completedExercises}>
        <h4>Result</h4>
        {results.map((result, i) => (
          <div key={`sec-${i}`}>
            <h5>Exercise {results.length - i}</h5>
            <Table striped>
              <tbody>
                <tr><td>Level</td><td className="text-right">{result.level}</td></tr>
                {result.gender && <tr><td>Gender</td><td className="text-right">{genderLabels[result.gender]}</td></tr>}
                <tr><td>Time</td><td className="text-right">{result.time}</td></tr>
                {result.attempts ? (<tr><td>Attempts</td><td className="text-right">{result.attempts}</td></tr>) : null}
              </tbody>
            </Table>
          </div>
        ))}
      </ExerciseComplete>
      <FooterButtons
        extraText={getCenteredBreathingText(status)}
        hidden={status === 'in-progress'}
        buttons={getCognitiveFooterButtons(
          status,
          pages.nameTheFace.title!,
          isReadyToStart(status, exercises),
          backTo,
          handleNext,
          handleRepeat,
          'Go to the next exercise',
          'Select a different level'
        )}
      />
    </>
  )
}

export default NtfExercises